<template>
  <!-- Intentionally invisible. This component is invoked via ref from Adjustments.vue -->
  <div style="display:none" />
</template>

<script>
import * as XLSX from 'xlsx-js-style';

export default {
  name: 'AdjustmentsExcel',

  props: {
    // period object from periods table: { id, name, ... }
    period: {
      type: Object,
      default: null,
    },

    // filtered models list from Adjustments.vue
    // Expected each model: { id, fullName, stageName, adjustments: [...] }
    // Expected each adjustment: { type, currency, description, amount, quotas, observation }
    models: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      paginaByModelId: {}
    };
  },

  methods: {
    normalizeExportGroup(group) {
      return group === 'cb' ? 'cb' : 'main';
    },
    getBonusCommissionPercent(adj, fallbackPercent = 100) {
      const hasOverride = adj?.commission_percent !== null &&
        adj?.commission_percent !== undefined &&
        adj?.commission_percent !== '';
      const overridePercent = Number(adj?.commission_percent);
      return hasOverride && Number.isFinite(overridePercent) ? overridePercent : fallbackPercent;
    },
    isMainSite(site) {
      return ['Flirt4Free', 'Streamate', 'LiveJasmin', 'Stripchat'].includes(site);
    },
    matchesExportGroup(site, group) {
      const normalizedSite = this.normalizeSiteName(site);
      const normalizedGroup = this.normalizeExportGroup(group);
      if (normalizedGroup === 'cb') return normalizedSite === 'Chaturbate';
      return this.isMainSite(normalizedSite);
    },
    normalizeDateLabel(dateValue) {
      if (!dateValue) return '';
      if (typeof dateValue === 'string') return dateValue.split('T')[0];
      if (dateValue instanceof Date && !isNaN(dateValue.getTime())) {
        const yyyy = dateValue.getFullYear();
        const mm = String(dateValue.getMonth() + 1).padStart(2, '0');
        const dd = String(dateValue.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
      }
      return String(dateValue);
    },
    buildExportDateSuffix(periodName, paymentDateLabel) {
      const normalizedDate = this.normalizeDateLabel(paymentDateLabel);
      if (!normalizedDate) return '';

      const [year, month, day] = normalizedDate.split('-');
      const safePeriodName = String(periodName || '');
      if (year && safePeriodName.endsWith(year) && month && day) {
        return `${month}-${day}`;
      }

      return normalizedDate;
    },

    async ensurePaginaMap() {
      // If already loaded, don't refetch
      if (this.paginaByModelId && Object.keys(this.paginaByModelId).length > 0) return;

      try {
        // Modato stores JWT in localStorage as `authToken`
        const storedUserRaw = localStorage.getItem('user');
        let storedUser = null;
        try { storedUser = storedUserRaw ? JSON.parse(storedUserRaw) : null; } catch (e) { storedUser = null; }

        const token = localStorage.getItem('authToken') || storedUser?.token || '';

        if (!token) {
          console.warn('AdjustmentsExcel: Missing auth session; cannot fetch /api/models for paginaAjuste');
          return;
        }

        const resp = await fetch('/api/models', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (!resp.ok) {
          console.warn('AdjustmentsExcel: Failed to fetch /api/models for paginaAjuste:', resp.status);
          return;
        }

        const models = await resp.json();
        const map = {};
        (models || []).forEach(m => {
          if (m && m.id != null) map[m.id] = m.paginaAjuste || '';
        });
        this.paginaByModelId = map;
      } catch (e) {
        console.warn('AdjustmentsExcel: Error fetching /api/models for paginaAjuste:', e?.message || e);
      }
    },

    resolvePaginaAjuste(model) {
      // Prefer the value already present in the adjustments payload
      if (model && model.paginaAjuste) return model.paginaAjuste;
      const id = model && model.id != null ? model.id : null;
      if (id == null) return '';
      return (this.paginaByModelId && this.paginaByModelId[id]) ? this.paginaByModelId[id] : '';
    },

    mapPaginaToJorge(value) {
      if (!value) return '';
      const map = {
        Flirt4Free: 'Flirt',
        Streamate: 'Stmate',
        Chaturbate: 'Chatur',
        Stripchat: 'Strip',
        StripChat: 'Strip',
        LiveJasmin: 'Jasmin'
      };
      return map[value] || '';
    },
    normalizeSiteName(value) {
      const raw = String(value || '').trim().toLowerCase();
      if (!raw) return null;
      const map = {
        f4f: 'Flirt4Free',
        flirt4free: 'Flirt4Free',
        sm: 'Streamate',
        streamate: 'Streamate',
        lj: 'LiveJasmin',
        livejasmin: 'LiveJasmin',
        sc: 'Stripchat',
        stripchat: 'Stripchat',
        cb: 'Chaturbate',
        chaturbate: 'Chaturbate'
      };
      return map[raw] || null;
    },
    resolveHighestMainSubsidySite(model) {
      const mainSites = new Set(['Flirt4Free', 'Streamate', 'LiveJasmin', 'Stripchat']);
      const explicitResolved = this.normalizeSiteName(model?.resolvedMainSubsidySite);
      if (explicitResolved && mainSites.has(explicitResolved)) return explicitResolved;

      const earnings = Array.isArray(model?.siteEarnings) ? model.siteEarnings : [];
      let bestSite = null;
      let bestGross = -Infinity;
      earnings.forEach((entry) => {
        const site = this.normalizeSiteName(entry?.site);
        const gross = Number(entry?.gross || 0);
        if (!site || !mainSites.has(site) || !Number.isFinite(gross)) return;
        if (gross > bestGross) {
          bestGross = gross;
          bestSite = site;
        }
      });
      if (bestSite && bestGross > 0) return bestSite;

      const pageSite = this.normalizeSiteName(this.resolvePaginaAjuste(model));
      if (pageSite && mainSites.has(pageSite)) return pageSite;
      return 'Flirt4Free';
    },

    async exportToXlsx(options = {}) {
      await this.ensurePaginaMap();
      const exportGroup = this.normalizeExportGroup(options?.group);
      const paymentDateLabel = this.normalizeDateLabel(
        options?.paymentDate || (exportGroup === 'cb' ? this.period?.cb_payment_date : this.period?.payment_date)
      );
      const noteLines = String(options?.note || '')
        .replace(/\r\n/g, '\n')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      const copHeaders = [
        'Buscar Por',
        'Termino Busqueda',
        'Pagina Ajuste',
        'Descripcion',
        'Total',
        'Cuotas',
        'Notas',
      ];
      const usdHeaders = [
        'Buscar Por',
        'Termino Busqueda',
        'Pagina Ajuste',
        'Descripcion',
        'Total',
        'RATE',
        'Cuotas',
        'Notas',
      ];

      const copRows = [
        ['AJUSTES EN PESOS'],
        copHeaders,
      ];
      const usdRows = [
        ['AJUSTES EN DOLARES'],
        usdHeaders,
      ];

      const safeModels = Array.isArray(options.models) ? options.models : Array.isArray(this.models) ? this.models : [];

      for (const model of safeModels) {
        const stageName = model?.stageName || '';
        const adjustments = Array.isArray(model?.adjustments) ? model.adjustments : [];

        for (const adj of adjustments) {
          if (adj?.nextPeriod || adj?.payout_status === 'awaiting_payment') continue;

          // Rules:
          // - Studio Bonus: export in USD or COP
          // - Site Bonus: paid in Payments, not exported for Jorge/Wiz
          // - Everything else: COP only
          const currency = (adj?.currency || '').toString().toUpperCase();
          const type = (adj?.type || '').toString();
          const typeLower = type.toLowerCase();
          const normalizedTypeKey = typeLower.replace(/[\s_-]+/g, '');
          const isStudioBonus = normalizedTypeKey === 'studiobonus';
          const isSiteBonus = normalizedTypeKey === 'sitebonus' || normalizedTypeKey === 'bonus';
          const description = adj?.description ?? '';
          const bonusCommissionPercent = this.getBonusCommissionPercent(adj, 100);

          if (isStudioBonus) {
            if (currency !== 'COP' && currency !== 'USD') continue;
          } else if (isSiteBonus) {
            continue;
          } else if (currency !== 'COP') {
            continue;
          }

          // Installments rule (Jorge): ONLY export first installment (number === 1)
          const typeLowerForRule = type.toLowerCase();
          const isInstallmentType = typeLowerForRule === 'purchase' || typeLowerForRule === 'discount';

          const installmentNumberRaw = adj?.number;
          const installmentNumber = Number(installmentNumberRaw);

          // If this is a purchase/discount AND it's not the first installment, skip it
          if (isInstallmentType && Number.isFinite(installmentNumber) && installmentNumber > 1) {
            continue;
          }

          const adjustmentSite = adj?.site || this.resolvePaginaAjuste(model);
          if (!this.matchesExportGroup(adjustmentSite, exportGroup)) {
            continue;
          }

          const observation = adj?.observation ?? '';
          const paginaAjuste = this.mapPaginaToJorge(adjustmentSite);

          // Amount must be numeric in Excel (keep sign)
          const amountRaw = adj?.amount;
          const amount = typeof amountRaw === 'number'
            ? amountRaw
            : Number(String(amountRaw ?? '').replace(/,/g, ''));

          // Flip sign in Excel based on type (amounts are stored positive in Modato)
          let signedAmount = Number.isFinite(amount) ? amount : 0;

          if (typeLower === 'purchase' || typeLower === 'discount') {
            signedAmount = -Math.abs(signedAmount);
          } else if (typeLower === 'adjustment') {
            signedAmount = Math.abs(signedAmount);
          } else if (isStudioBonus && currency === 'COP') {
            signedAmount = Math.abs(signedAmount) * (bonusCommissionPercent / 100);
          }

          // Quotas default to 1 if missing/invalid
          const quotasRaw = adj?.quotas;
          const quotasNum = Number(quotasRaw);
          const quotas = Number.isFinite(quotasNum) && quotasNum > 0 ? quotasNum : 1;

          const baseRow = [
            'Nombre Artistico',
            stageName,
            paginaAjuste, // Pagina Ajuste (Jorge code)
            description,
            signedAmount,
            quotas,
            observation,
          ];

          if (currency === 'USD') {
            usdRows.push([
              baseRow[0],
              baseRow[1],
              baseRow[2],
              baseRow[3],
              baseRow[4],
              isStudioBonus ? `${bonusCommissionPercent}%` : '',
              baseRow[5],
              baseRow[6],
            ]);
          } else {
            copRows.push(baseRow);
          }
        }

        // Subsidy export (as its own row)
        const subsidy = String(model?.subsidy || 'None').trim();
        const subsidyLower = subsidy.toLowerCase();
        const subsidyAmount = subsidyLower.includes('half')
          ? 15000
          : subsidyLower.includes('full')
            ? 30000
            : 0;
        const subsidyIsCb = subsidyLower === 'full - cb' || subsidyLower === 'half - cb';
        const shouldExportSubsidy = subsidyAmount > 0 && (
          (exportGroup === 'cb' && subsidyIsCb) ||
          (exportGroup === 'main' && !subsidyIsCb)
        );

        if (shouldExportSubsidy) {
          const subsidySite = subsidyIsCb
            ? 'Chaturbate'
            : this.resolveHighestMainSubsidySite(model);
          const subsidyPaginaAjuste = this.mapPaginaToJorge(subsidySite);
          copRows.push([
            'Nombre Artistico',
            stageName,
            subsidyPaginaAjuste,
            'Subsidio de Transporte',
            subsidyAmount,
            1,
            '',
          ]);
        }
      }

      if (noteLines.length > 0) {
        copRows.push([]);
        copRows.push(['NOTAS', '', '', '', '', '', '']);
        noteLines.forEach((line) => {
          copRows.push([line, '', '', '', '', '', '']);
        });
      }

      // Create worksheets
      const copWs = XLSX.utils.aoa_to_sheet(copRows);
      const usdWs = XLSX.utils.aoa_to_sheet(usdRows);

      // Merge title row A1:G1 on both sheets
      copWs['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];
      usdWs['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 6 } }];

      // Title styling (black + bold + centered) with requested fills
      copWs.A1 = copWs.A1 || { t: 's', v: 'AJUSTES EN PESOS' };
      usdWs.A1 = usdWs.A1 || { t: 's', v: 'AJUSTES EN DOLARES' };
      copWs.A1.s = {
        font: { bold: true, color: { rgb: '000000' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        fill: { patternType: 'solid', fgColor: { rgb: 'EBEFDD' } } // Olive Green accent 3 lighter 80%
      };
      usdWs.A1.s = {
        font: { bold: true, color: { rgb: '000000' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        fill: { patternType: 'solid', fgColor: { rgb: 'E4F3F7' } } // Aqua accent 5 lighter 80%
      };

      // Column widths (nice readability)
      copWs['!cols'] = [
        { wch: 16 }, // Buscar Por
        { wch: 22 }, // Termino Busqueda
        { wch: 18 }, // Pagina Ajuste
        { wch: 40 }, // Descripcion
        { wch: 14 }, // Total
        { wch: 10 }, // Cuotas
        { wch: 35 }, // Notas
      ];
      usdWs['!cols'] = [
        { wch: 16 }, // Buscar Por
        { wch: 22 }, // Termino Busqueda
        { wch: 18 }, // Pagina Ajuste
        { wch: 40 }, // Descripcion
        { wch: 14 }, // Total
        { wch: 10 }, // RATE
        { wch: 10 }, // Cuotas
        { wch: 35 }, // Notas
      ];

      // Auto-filter on header row (row 2)
      copWs['!autofilter'] = { ref: 'A2:G2' };
      usdWs['!autofilter'] = { ref: 'A2:H2' };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, copWs, 'COP');
      XLSX.utils.book_append_sheet(wb, usdWs, 'USD');

      // Get studio from logged-in user (localStorage is source of truth)
      let studioCode = 'STUDIO';

      try {
        const storedUser = JSON.parse(localStorage.getItem('user'));
        const studioId = storedUser?.studio_id;
        const role = storedUser?.role;

        // Satellite monitor exports should be labeled as SAT
        if (role === 'satellite_monitor') {
          studioCode = 'SAT';
        } else {
          const studioMap = {
            1: 'CC',
            2: 'CJ',
            3: 'BOG',
            4: 'PEI',
          };

          studioCode = studioMap[studioId] || (studioId ? `S${studioId}` : 'STUDIO');
        }
      } catch (err) {
        // fallback stays as STUDIO
      }

      const periodName = this.period?.name || 'Period';
      const safePeriodName = String(periodName).replace(/[\\/:*?"<>|]/g, '-');
      const groupLabel = exportGroup === 'cb' ? 'CB' : 'Main';
      const exportDateSuffix = this.buildExportDateSuffix(periodName, paymentDateLabel);
      const fileName = `Ajustes_${studioCode}(${groupLabel})-${safePeriodName}${exportDateSuffix ? `-${exportDateSuffix}` : ''}.xlsx`;

      // Triggers browser download (enable style serialization when supported)
      XLSX.writeFile(wb, fileName, { cellStyles: true });
      return true;
    },
  },
};
</script>
